import { Viewer, Web3d } from "hello-mono-sdk/packages/web-3d";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ViewerLiteRefType } from "../../types/frontend";

interface Viewer3DLiteProps {
  modelDetails: { glbUrl: string };
  lights?: any[];
  envMap?: { map: string; intensity?: number };
  shadow?: { enabled?: boolean; intensity?: number; softness?: number };
  onSceneLoaded?: () => void;
}

const DEFAULT_ENV_MAP = {
  map: "https://dlz0qy8t8ufft.cloudfront.net/maps/png/photo_studio.jpg",
  name: "Photo Studio",
  thumbnail: "https://dlz0qy8t8ufft.cloudfront.net/maps/png/photo_studio.jpg",
  format: ".png",
};

const Viewer3DLite = React.forwardRef<ViewerLiteRefType, Viewer3DLiteProps>(
  (props, ref) => {
    const [is3dSceneLoading, setIs3dSceneLoading] = useState(true);
    const [sceneLoadingProgress, setSceneLoadingProgress] = useState(0);
    const [threeDeeError, setThreeDeeError] = useState<string>();
    const viewerRef = useRef<Viewer>();
    const isInitialized = useRef<boolean>(false);

    const destroy3D = useCallback(() => {
      viewerRef.current?.destroy();
      viewerRef.current = undefined;
      setIs3dSceneLoading(true);
      setSceneLoadingProgress(0);
      setThreeDeeError(undefined);
      isInitialized.current = false;
    }, []);

    useEffect(() => {
      return () => {
        destroy3D();
      };
    }, [destroy3D]);

    useImperativeHandle(
      ref,
      () => {
        return {
          getCore3D: () => {
            const core = viewerRef.current?.getWeb3d();
            return core;
          },
          getViewer: () => {
            const viewer = viewerRef.current
            return viewer;
          }
        };
      },
      []
    );

    const onDivMounted = useCallback(
      async (div: HTMLDivElement) => {
        if (isInitialized.current) return;
        try {
          setThreeDeeError(undefined);
          setIs3dSceneLoading(true);
          const glbUrl = props.modelDetails.glbUrl;
          let lights = props.lights || [];
          let lightsToUpdatePosition: any[] = [];
          let envMapMeta = props.envMap;

          if (glbUrl && div) {
            const viewer = new Viewer({ parent: div });
            const web3d = viewer.getWeb3d();
            const lightingManager = web3d.getLightingManager();
            viewerRef.current = viewer;
            const defaultLights = Web3d.getDefaultLights();
            if (lights.length === 0) {
              lights = defaultLights;
              lightsToUpdatePosition = defaultLights;
            }
            if (!envMapMeta) {
              envMapMeta = DEFAULT_ENV_MAP;
            }
            await viewer.init({
              modelDetails: {
                glb: glbUrl,
              },
              settings: {
                lights,
                envMapMeta,
                shadow: props.shadow,
              },
              onProgress: (progress) => {
                setSceneLoadingProgress(progress);
              },
            });

            if (lightsToUpdatePosition.length > 0) {
              lightingManager.updateLightsPosition(lightsToUpdatePosition);
            }

            props.onSceneLoaded && props.onSceneLoaded();
            setIs3dSceneLoading(false);
            isInitialized.current = true;
          } else {
            setThreeDeeError("Cannot Initialize 3D Scene");
            console.error(
              "Cannot Initialize 3D Scene",
              "No GLB URL or Parent Div Found"
            );
          }
        } catch (err) {
          setThreeDeeError("Cannot Initialize 3D Scene");
          console.error("Cannot Initialize 3D Scene", err);
        }
      },
      [isInitialized]
    );

    return (
      <div className="helloar-reserved">
        {is3dSceneLoading && (
        <div className="loader-container">
          <div className="loader-containing-wrapper">
            <div className="loader-wrapper">
              <div
                className="loader-outline"
                style={{
                  backgroundColor: "green",
                }}
              >
                <div
                  className="loader-progress"
                  style={{ width: `${sceneLoadingProgress}%` }}
                />
                <span className="loader-text">{sceneLoadingProgress}%</span>
              </div>
            </div>
          </div>
        </div>
      )}
        <div className="viewer full-width">
          {threeDeeError && <p>{threeDeeError}</p>}
          <div
            id="hello-div"
            ref={onDivMounted}
            style={{
              width: "100%",
              height: "100%",
            }}
          ></div>
        </div>
      </div>
    );
  }
);

export default Viewer3DLite;
